import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

const FilterBar = ({ filterComponents, withBottomMargin }) => {
  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10); // default for desktop
  // A filter decides for itself whether it has anything to offer — a hotel filter renders nothing on a
  // tour search, for instance — and only what it actually drew should take a slot on the bar or a gap
  // between pills.
  const [drawnKeys, setDrawnKeys] = useState([]);
  const wrappers = useRef({});
  const bar = useRef(null);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width < 768) {
        // Small tablets and mobile devices
        setVisibleCount(2);
      } else if (width < 900) {
        // Medium tablets / small laptops
        setVisibleCount(3);
      } else if (width < 1080) {
        // Just below desktop
        setVisibleCount(5);
      } else {
        // Full desktop and up
        setVisibleCount(10);
      }
    };

    updateVisibleCount(); // on mount
    window.addEventListener('resize', updateVisibleCount);

    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const measure = useCallback(() => {
    const drawn = filterComponents
      .filter((filter) => (wrappers.current[filter.key]?.childElementCount || 0) > 0)
      .map((filter) => filter.key);
    setDrawnKeys((previous) =>
      previous.length === drawn.length && previous.every((key, index) => key === drawn[index])
        ? previous
        : drawn,
    );
  }, [filterComponents]);

  useEffect(measure);

  // A filter can start or stop drawing on its own, from a store it alone listens to, without this bar
  // re-rendering — the compare button appears that way — so its subtree is watched to measure again.
  useEffect(() => {
    if (!bar.current || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const observer = new MutationObserver(measure);
    observer.observe(bar.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [measure]);

  const visibleKeys = showAll ? drawnKeys : drawnKeys.slice(0, visibleCount);
  const shouldShowToggle = drawnKeys.length > visibleCount;

  return (
    <div className="w-full">
      <div ref={bar} className={clsx('flex flex-wrap gap-1', { 'pb-5': withBottomMargin })}>
        {filterComponents.map((filter) => (
          <div
            key={filter.key}
            ref={(element) => {
              wrappers.current[filter.key] = element;
            }}
            className={clsx('inline-block whitespace-nowrap', {
              // Hidden rather than unmounted: a filter has to render before it can say it has nothing
              // to show, and unmounting it would make it say so again on the next render, forever.
              hidden: !visibleKeys.includes(filter.key),
            })}
          >
            {filter.element}
          </div>
        ))}

        {shouldShowToggle && (
          <button
            className="px-3 py-2 border rounded-2xl bg-white text-sm flex-shrink-0"
            onClick={() => setShowAll((prev) => !prev)}
            type="button"
          >
            {showAll ? 'Show Less' : 'More Filters'}
          </button>
        )}
      </div>
    </div>
  );
};

FilterBar.propTypes = {
  filterComponents: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      element: PropTypes.node,
    }),
  ).isRequired,
  withBottomMargin: PropTypes.bool.isRequired,
};

export default FilterBar;
